import { CommonModule } from '@angular/common';
import {
  Component,
  Input,
  input,
  Output,
  EventEmitter,
  ChangeDetectionStrategy,
  signal,
  computed,
  OnInit,
  OnChanges,
  SimpleChanges,
  inject,
  DestroyRef,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { BreakpointObserver } from '@angular/cdk/layout';
import { MatIconModule } from '@angular/material/icon';

import {
  ScheduleSlot,
  ScheduleSlotsResponse,
} from '../../../core/models/schedule.model';
import { I18nPipe } from '../../../shared/pipes/i18n.pipe';
import { I18nService } from '../../../core/services/i18n.service';
import { ScheduleService } from '../../../core/services/schedule.service';
import { CardWrapperComponent } from '../../../shared/components/card-wrapper/card-wrapper.component';

export interface DateGroup {
  dateKey: string;
  day: number;
  month: number;
  year: number;
  label: string;
  dayOfWeekIndex: number;
  slots: ScheduleSlot[];
}

const WEEKDAY_KEYS = [
  'components.schedulePicker.weekday0',
  'components.schedulePicker.weekday1',
  'components.schedulePicker.weekday2',
  'components.schedulePicker.weekday3',
  'components.schedulePicker.weekday4',
  'components.schedulePicker.weekday5',
  'components.schedulePicker.weekday6',
] as const;

export interface ScheduleSelections {
  firstOption: ScheduleSlot;
  secondOption: ScheduleSlot;
}

@Component({
  selector: 'umkt-schedule-picker-card',
  standalone: true,
  imports: [CommonModule, MatIconModule, I18nPipe, CardWrapperComponent],
  templateUrl: './schedule-picker-card.component.html',
  styleUrls: ['./schedule-picker-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SchedulePickerCardComponent implements OnInit, OnChanges {
  @Input() set slotsResponse(value: ScheduleSlotsResponse | null | undefined) {
    this._slotsResponse.set(value ?? null);
  }
  @Input() disableAutoFetch = false;
  @Input() isEditing = false;
  @Input() selections: ScheduleSelections | null = null;
  @Input() canGoToNextStep = false;
  @Input() isCardLoading = false;
  /** Quando true (ex.: edição a partir da tela de revisão), o botão exibe "Cancelar" em vez de "Voltar" */
  @Input() useCancelInsteadOfBack = false;

  @Output() nextStep = new EventEmitter<void>();
  @Output() scheduleSubmit = new EventEmitter<ScheduleSelections>();
  @Output() previousStep = new EventEmitter<void>();
  @Output() insufficientSlotsChange = new EventEmitter<boolean>();
  @Output() editingStateChange = new EventEmitter<boolean>();

  readonly optionLabels = input<string[]>([]);
  readonly optionLabelKeys = input<string[]>([
    'components.schedulePicker.firstOption',
    'components.schedulePicker.secondOption',
  ]);

  private readonly _slotsResponse = signal<ScheduleSlotsResponse | null>(null);
  private readonly breakpointObserver = inject(BreakpointObserver);
  private readonly destroyRef = inject(DestroyRef);
  private readonly i18nService = inject(I18nService);
  private readonly scheduleService = inject(ScheduleService);

  readonly isLoadingSlots = signal<boolean>(false);
  readonly visibleDatesCount = signal<number>(5);

  private readonly _currentIndices = signal<number[]>([0, 0]);
  private readonly _selectedDateKeys = signal<(string | null)[]>([null, null]);
  private readonly _selectedSlots = signal<(ScheduleSlot | null)[]>([
    null,
    null,
  ]);

  readonly dateGroups = computed(() => {
    const data = this._slotsResponse();
    const slots = data?.slots ?? [];
    return this.groupSlotsByDate(slots);
  });

  readonly insufficientSlots = computed(() => {
    const data = this._slotsResponse();
    if (!data) return true;
    return data.insufficientSlots;
  });

  readonly options = computed(() => {
    const labels = this.optionLabels();
    if (labels?.length) {
      return labels.map((label, index) => ({
        labelKey: null as string | null,
        label,
        index,
      }));
    }
    return this.optionLabelKeys().map((labelKey, index) => ({
      labelKey,
      label: null as string | null,
      index,
    }));
  });

  getOptionLabel(opt: {
    labelKey: string | null;
    label: string | null;
    index: number;
  }): string {
    return opt.labelKey
      ? this.i18nService.instant(opt.labelKey)
      : (opt.label ?? '');
  }

  getDayOfWeekShort(dayOfWeekIndex: number): string {
    const key = WEEKDAY_KEYS[dayOfWeekIndex];
    return key ? this.i18nService.instant(key) : '';
  }

  readonly canSubmit = computed(() => {
    const slots = this._selectedSlots();
    return slots.every(s => s != null);
  });

  readonly showSlotsLoading = computed(
    () =>
      this.isEditing &&
      (this.isLoadingSlots() ||
        (this.disableAutoFetch && this._slotsResponse() === null))
  );

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['selections']) {
      this.syncStateFromSelections();
    }
  }

  ngOnInit(): void {
    this.insufficientSlotsChange.emit(this.insufficientSlots());
    this.breakpointObserver
      .observe(['(max-width: 600px)', '(max-width: 880px)'])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(state => {
        const is600 = state.breakpoints['(max-width: 600px)'];
        const is880 = state.breakpoints['(max-width: 880px)'];
        const count = is600 ? 3 : is880 ? 4 : 5;
        this.visibleDatesCount.set(count);
      });

    if (!this.disableAutoFetch && this._slotsResponse() === null) {
      this.isLoadingSlots.set(true);
      this.scheduleService
        .getAvailableSlots()
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: response => {
            this._slotsResponse.set(response);
            this.insufficientSlotsChange.emit(response.insufficientSlots);
            this.isLoadingSlots.set(false);
            this.syncStateFromSelections();
          },
          error: () => {
            this.isLoadingSlots.set(false);
          },
        });
    } else {
      this.syncStateFromSelections();
    }
  }

  private syncStateFromSelections(): void {
    const sel = this.selections;
    if (!sel?.firstOption || !sel?.secondOption) return;
    const data = this._slotsResponse();
    if (!data?.slots?.length) return;
    const dateKeyFromSlot = (slot: ScheduleSlot) => {
      const d = new Date(slot.startDate);
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    };
    
    const toTime = (iso: string) => new Date(iso).getTime();
    const findInResponse = (slot: ScheduleSlot) => {
      const wantedTime = toTime(slot.startDate);
      return (
        data.slots.find(s => {
          const t = toTime(s.startDate);
          const timeMatches =
            Number.isFinite(wantedTime) && Number.isFinite(t)
              ? t === wantedTime
              : s.startDate === slot.startDate;
          return timeMatches && s.periodTypeId === slot.periodTypeId;
        }) ?? null
      );
    };
    const first = findInResponse(sel.firstOption) ?? sel.firstOption;
    const second = findInResponse(sel.secondOption) ?? sel.secondOption;
    this._selectedDateKeys.set([
      dateKeyFromSlot(first),
      dateKeyFromSlot(second),
    ]);
    this._selectedSlots.set([first, second]);
  }

  private groupSlotsByDate(slots: ScheduleSlot[]): DateGroup[] {
    const map = new Map<string, DateGroup>();
    for (const slot of slots) {
      const d = new Date(slot.startDate);
      const dateKey = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      if (!map.has(dateKey)) {
        map.set(dateKey, {
          dateKey,
          day: d.getDate(),
          month: d.getMonth() + 1,
          year: d.getFullYear(),
          label: `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}`,
          dayOfWeekIndex: d.getDay(),
          slots: [],
        });
      }
      map.get(dateKey)!.slots.push(slot);
    }
    return Array.from(map.values()).sort(
      (a, b) => new Date(a.dateKey).getTime() - new Date(b.dateKey).getTime()
    );
  }

  getAvailableDateGroups(optionIndex: number): DateGroup[] {
    const groups = this.dateGroups();
    const otherIndex = optionIndex === 0 ? 1 : 0;
    const selectedKeyOther = this._selectedDateKeys()[otherIndex] ?? null;
    return groups.filter(
      g => !(g.slots.length === 1 && selectedKeyOther === g.dateKey)
    );
  }

  getVisibleDates(optionIndex: number): DateGroup[] {
    const available = this.getAvailableDateGroups(optionIndex);
    const visible = this.visibleDatesCount();
    const idx = this._currentIndices()[optionIndex] ?? 0;
    const start = Math.min(idx, Math.max(0, available.length - visible));
    const end = Math.min(start + visible, available.length);
    return available.slice(start, end);
  }

  canGoPrev(optionIndex: number): boolean {
    const available = this.getAvailableDateGroups(optionIndex);
    const visible = this.visibleDatesCount();
    const idx = this._currentIndices()[optionIndex] ?? 0;
    const start = Math.min(idx, Math.max(0, available.length - visible));
    return start > 0;
  }

  canGoNext(optionIndex: number): boolean {
    const available = this.getAvailableDateGroups(optionIndex);
    const visible = this.visibleDatesCount();
    const idx = this._currentIndices()[optionIndex] ?? 0;
    const start = Math.min(idx, Math.max(0, available.length - visible));
    return start + visible < available.length;
  }

  getSelectedDateKey(optionIndex: number): string | null {
    return this._selectedDateKeys()[optionIndex] ?? null;
  }

  getSlotsForDate(optionIndex: number): ScheduleSlot[] {
    const key = this.getSelectedDateKey(optionIndex);
    if (!key) return [];
    const group = this.dateGroups().find(g => g.dateKey === key);
    const slots = group?.slots ?? [];
    const otherIndex = optionIndex === 0 ? 1 : 0;
    const otherSlot = this._selectedSlots()[otherIndex];
    if (!otherSlot || this.getSelectedDateKey(otherIndex) !== key) {
      return slots;
    }
    return slots.filter(
      s =>
        !(
          s.startDate === otherSlot.startDate &&
          s.periodTypeId === otherSlot.periodTypeId
        )
    );
  }

  getSelectedSlotValue(optionIndex: number): string {
    const slot = this._selectedSlots()[optionIndex];
    return slot ? this.slotValue(slot) : '';
  }

  selectDate(optionIndex: number, group: DateGroup): void {
    const otherIndex = optionIndex === 0 ? 1 : 0;
    const isSinglePeriod = group.slots.length === 1;
    const otherHadThisDate =
      this._selectedDateKeys()[otherIndex] === group.dateKey;

    this._selectedDateKeys.update(keys => {
      const next = [...keys];
      next[optionIndex] = group.dateKey;
      if (isSinglePeriod && otherHadThisDate) {
        next[otherIndex] = null;
      }
      return next;
    });
    this._selectedSlots.update(slots => {
      const next = [...slots];
      next[optionIndex] = null;
      if (isSinglePeriod && otherHadThisDate) {
        next[otherIndex] = null;
      }
      return next;
    });
  }

  prevDates(optionIndex: number): void {
    if (!this.canGoPrev(optionIndex)) return;
    this._currentIndices.update(indices => {
      const next = [...indices];
      next[optionIndex] = (next[optionIndex] ?? 0) - 1;
      return next;
    });
  }

  nextDates(optionIndex: number): void {
    if (!this.canGoNext(optionIndex)) return;
    this._currentIndices.update(indices => {
      const next = [...indices];
      next[optionIndex] = (next[optionIndex] ?? 0) + 1;
      return next;
    });
  }

  selectPeriod(optionIndex: number, slot: ScheduleSlot): void {
    const otherIndex = optionIndex === 0 ? 1 : 0;
    this._selectedSlots.update(slots => {
      const next = [...slots];
      next[optionIndex] = slot;
      const otherSlot = slots[otherIndex];
      if (
        otherSlot &&
        otherSlot.startDate === slot.startDate &&
        otherSlot.periodTypeId === slot.periodTypeId
      ) {
        next[otherIndex] = null;
      }
      return next;
    });
  }

  getPeriodLabel(periodTypeId: number): string {
    const key =
      periodTypeId === 1
        ? 'components.schedulePicker.periodMorning'
        : periodTypeId === 2
          ? 'components.schedulePicker.periodAfternoon'
          : null;
    return key ? this.i18nService.instant(key) : String(periodTypeId);
  }

  private formatTimeForLocale(date: Date): string {
    const locale = this.i18nService.getCurrentLanguageSync();
    if (locale === 'en-US') {
      const hours = date.getHours();
      const minutes = date.getMinutes();
      const h12 = hours % 12 || 12;
      const ampm = hours < 12 ? 'AM' : 'PM';
      const min = String(minutes).padStart(2, '0');
      return `${h12}:${min} ${ampm}`;
    }
    return `${String(date.getHours()).padStart(2, '0')}h`;
  }

  slotOptionLabel(slot: ScheduleSlot): string {
    const start = new Date(slot.startDate);
    const end = new Date(slot.endDate);
    const periodLabel = this.getPeriodLabel(slot.periodTypeId);
    const timeRange = this.i18nService.instant(
      'components.schedulePicker.periodTimeRange',
      {
        start: this.formatTimeForLocale(start),
        end: this.formatTimeForLocale(end),
      }
    );
    return `${periodLabel} - ${timeRange}`;
  }

  slotValue(slot: ScheduleSlot): string {
    return `${slot.startDate}|${slot.periodTypeId}`;
  }

  findSlotByValue(optionIndex: number, value: string): ScheduleSlot | null {
    const slots = this.getSlotsForDate(optionIndex);
    const [startDate, periodIdStr] = value.split('|');
    const periodTypeId = parseInt(periodIdStr, 10);
    return (
      slots.find(
        s => s.startDate === startDate && s.periodTypeId === periodTypeId
      ) ?? null
    );
  }

  onPeriodChange(optionIndex: number, event: Event): void {
    const el = event.target as HTMLSelectElement;
    const value = el.value;
    if (!value) {
      this._selectedSlots.update(slots => {
        const next = [...slots];
        next[optionIndex] = null;
        return next;
      });
      return;
    }
    const slot = this.findSlotByValue(optionIndex, value);
    if (slot) this.selectPeriod(optionIndex, slot);
  }

  getSelections(): ScheduleSelections | null {
    const slots = this._selectedSlots();
    if (slots.length >= 2 && slots[0] && slots[1]) {
      return { firstOption: slots[0], secondOption: slots[1] };
    }
    return null;
  }

  getDisplaySelections(): ScheduleSelections | null {
    return this.selections ?? this.getSelections();
  }

  getDateLabel(slot: ScheduleSlot): string {
    const d = new Date(slot.startDate);
    return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
  }

  onContinue(): void {
    const selections = this.getSelections();
    if (selections) this.scheduleSubmit.emit(selections);
  }

  startEditing(): void {
    this.editingStateChange.emit(true);
  }
}
